import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisV, FaSignOutAlt, FaChevronRight } from 'react-icons/fa';
import useAuth from '../../../configurations/auth/publicUsers/useAuth';

const PublicUserMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const { authentication, logedOut } = useAuth();

    const displayName = authentication?.data?.fullName || authentication?.data?.phoneNo || 'User';
    const phoneNo = authentication?.data?.phoneNo;
    const hasFullName = Boolean(authentication?.data?.fullName);

    // Naam ke initials nikalo avatar ke liye (e.g. "Rahul Kumar" -> "RK")
    const initials = hasFullName
        ? displayName
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w[0])
              .join('')
              .toUpperCase()
        : '#';

    // Bahar click ya Escape dabane par menu band ho jaye
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    return (
        <div className="relative" ref={menuRef}>
                {/* Trigger Button */}
                <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-label="User menu"
                    className={`p-2.5 rounded-full transition-all duration-200 active:scale-90
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2
                        ${isOpen
                            ? 'bg-brand-50 text-brand-500 shadow-inner'
                            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                        }`}
                >
                    <FaEllipsisV className="text-lg" />
                </button>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div
                        role="menu"
                        className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] origin-top-right rounded-2xl bg-white/95 backdrop-blur-xl
                            shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] ring-1 ring-slate-900/5 overflow-hidden
                            animate-in fade-in slide-in-from-top-2 zoom-in-95 duration-200"
                    >
                        {/* Profile Header — gradient strip ke saath */}
                        <div className="relative px-4 pt-5 pb-4 bg-gradient-to-br from-brand-400/10 via-transparent to-transparent">
                            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-400" />
                            <div className="flex items-center gap-3">
                                {/* Avatar with initials */}
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                                    bg-gradient-to-br from-brand-400 to-brand-600 text-white font-semibold text-sm
                                    shadow-md shadow-brand-400/30 ring-2 ring-white">
                                    {initials}
                                </div>
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-slate-800">
                                        {displayName}
                                    </p>
                                    {hasFullName && phoneNo && (
                                        <p className="truncate text-xs text-slate-500 mt-0.5">
                                            {phoneNo}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <hr className="border-slate-100" />

                        {/* Menu Items */}
                        <div className="p-2">
                            {/* Yahan future me aur options add kar sakte ho, e.g.:
                            <Link
                                to="/"
                                role="menuitem"
                                onClick={() => setIsOpen(false)}
                                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-700
                                    hover:bg-slate-50 transition-colors"
                            >
                                <FaHome className="text-slate-400 group-hover:text-brand-400 transition-colors" />
                                <span className="flex-1 text-left">Home</span>
                                <FaChevronRight className="text-[10px] text-slate-300 opacity-0 -translate-x-1
                                    group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            </Link>
                            */}

                            {/* Logout */}
                            <button
                                role="menuitem"
                                onClick={() => {
                                    setIsOpen(false);
                                    logedOut();
                                }}
                                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium
                                    text-rose-600 hover:bg-rose-50 active:bg-rose-100 transition-colors
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                            >
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50
                                    group-hover:bg-rose-100 transition-colors">
                                    <FaSignOutAlt className="text-rose-500" />
                                </span>
                                <span className="flex-1 text-left">Logout</span>
                                <FaChevronRight className="text-[10px] text-rose-300 opacity-0 -translate-x-1
                                    group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            </button>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default PublicUserMenu;